import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import {
  FileCollection,
  validateMaxFile,
  validateTypeFile,
  validationSizeFile,
} from '../../../../../utils/validationForm';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues, useForm, UseFormReturn, UseFormSetValue } from 'react-hook-form';
import { useNewExemption } from '../../../../../services/hooks/Exemptions/UseNewExemption';
import MessageError from '../../../../../components/MessageError';
import { IoChevronDownCircleOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import BoneMarrowUploadFields from './BoneMarrowUploadFields';
import { getBoneMarrowFileRoles } from '../../../../../services/hooks/Exemptions/boneMarrowDocuments';

interface IMarrowDonnorProps {
  attachmentExemption?: FileCollection;
  redome_code?: string;
}

interface IOwnProps {
  exemptionId: string;
  registrationId: string;
  redomeRequired?: boolean;
  requirementsLoaded?: boolean;
  onSetValue?: UseFormSetValue<FieldValues>;
  hookForms?: UseFormReturn<IMarrowDonnorProps>;
  redome_code?: string;
}

const MarrowDonnor: React.FC<IOwnProps> = ({
  exemptionId,
  registrationId,
  redomeRequired = false,
  requirementsLoaded = true,
  onSetValue,
  hookForms,
  redome_code,
}) => {
  const newExemption = useNewExemption();
  const toast = useToast();
  const navigate = useNavigate();

  const schema = Yup.object().shape({
    redome_code: Yup.string().test(
      'redome-code-required',
      'Informe o código de validação do REDOME.',
      (value) => !redomeRequired || Boolean(value?.trim()),
    ),
    attachmentExemption: Yup.mixed<FileCollection>()
      .test('Required', 'Preencha os três campos de documentos obrigatórios do doador de medula.', (file) => {
        const listFile = file as FileCollection;

        return listFile?.length === 3;
      })
      .test('validationSizeFile', 'Tamanho máximo para arquivos é de 2MB', (file) => {
        const listFile = file as FileCollection;

        if (listFile?.length > 0) {
          return validationSizeFile(listFile);
        }

        return true;
      })
      .test('validationMaxFile', 'Quantidade máxima de arquivos é de 5 arquivos', (file) => {
        const listFile = file as FileCollection;

        if (listFile?.length > 0) {
          return validateMaxFile(listFile);
        }
        return true;
      })
      .test('validationTypeFile', 'Formato de arquivo não suportado', (file) => {
        const listFile = file as FileCollection;

        if (listFile?.length > 0) {
          return validateTypeFile(listFile);
        }
        return true;
      }),
  });

  const { register, handleSubmit, formState, setValue, watch } = useForm({
    resolver: yupResolver(schema),
  });

  const initialFiles = (hookForms?.getValues('attachmentExemption') ?? watch('attachmentExemption')) as
    | FileCollection
    | undefined;
  const [selectedFiles, setSelectedFiles] = useState<(File | undefined)[]>(() => {
    const files = Array.from(initialFiles || []);
    return [files[0], files[1], files[2]];
  });

  const updateSelectedFile = (index: number, file?: File) => {
    const updatedSlots = [...selectedFiles];
    updatedSlots[index] = file;
    setSelectedFiles(updatedSlots);
    const files = updatedSlots.filter((selectedFile): selectedFile is File => selectedFile !== undefined);

    const updateOptions = { shouldDirty: true, shouldTouch: true, shouldValidate: true };
    if (hookForms) {
      hookForms.setValue('attachmentExemption', files, updateOptions);
      void hookForms.trigger('attachmentExemption');
    } else {
      setValue('attachmentExemption', files, updateOptions);
    }
  };

  const handleGoToCandidateArea = (data: FieldValues) => {
    exemptionSolicitation(data, '/painel/isencoes');
  };

  const handleCreateNewExemption = (data: FieldValues) => {
    exemptionSolicitation(data);
  };

  const exemptionSolicitation = (data: FieldValues, route?: string) => {
    newExemption
      .mutateAsync({
        ...data,
        type: exemptionId,
        registration: registrationId,
        file_roles: getBoneMarrowFileRoles(data.attachmentExemption as FileCollection | undefined),
      })
      .then(() => {
        toast({
          title: 'Sucesso',
          description: 'Pedido de isenção enviado com sucesso',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        setSelectedFiles([undefined, undefined, undefined]);
        if (hookForms) {
          hookForms.setValue('attachmentExemption', [], { shouldValidate: false });
        } else {
          setValue('attachmentExemption', [], { shouldValidate: false });
        }
        if (route) {
          navigate(route);
        }
      })
      .catch(() => {
        toast({
          title: 'Erro',
          description: 'Erro na solicitação, tente novamente',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const hasAttachmentError =
    hookForms?.formState?.errors?.attachmentExemption?.message ?? formState.errors.attachmentExemption?.message;
  const redomeCodeError = hookForms?.formState.errors.redome_code?.message ?? formState.errors.redome_code?.message;

  return (
    <Stack p={0} m={0} w={'50%'}>
      <Flex
        as="form"
        // onSubmit={handleSubmit(exemptionSolicitation)}
        rounded={4}
        flexDir="column"
        borderRadius="4px"
        gap={5}
      >
        <FormControl>
          <HStack spacing={3} mb={2}>
            <FormLabel mb={0}>
              Código de validação do REDOME{redomeRequired && <Text as="span" color="red.500"> *</Text>}
            </FormLabel>
          </HStack>
          <Input
            {...(hookForms ? hookForms.register('redome_code') : register('redome_code'))}
            defaultValue={redome_code}
            border="1px solid"
            borderColor={redomeCodeError ? 'red' : 'gray.200'}
            borderRadius="8px"
            _hover={{ borderColor: 'gray.300' }}
            _focus={{ borderColor: 'gray.300', boxShadow: 'none' }}
            fontSize="sm"
          />

          <MessageError>{redomeCodeError?.toString()}</MessageError>
          <Text>
            Informe o código de validação da sua declaração de doador de medula óssea emitida pelo REDOME, disponível no
            rodapé do documento
          </Text>
        </FormControl>

        <BoneMarrowUploadFields
          files={selectedFiles}
          hasError={Boolean(hasAttachmentError)}
          errorMessage={hasAttachmentError?.toString()}
          onSelectFile={updateSelectedFile}
          onRemoveFile={(index) => updateSelectedFile(index)}
        />

        {!hookForms && !onSetValue && (
          <Stack align="flex-end">
            <Menu>
              <MenuButton
                isDisabled={newExemption.isPending || !requirementsLoaded}
                bg="primaryColor"
                color="white"
                _hover="none"
                as={Button}
                rightIcon={<IoChevronDownCircleOutline />}
              >
                {newExemption.isPending ? <Spinner /> : 'Enviar'}
              </MenuButton>
              <MenuList>
                <MenuItem as="button" onClick={handleSubmit(handleCreateNewExemption)}>
                  Enviar e permanecer na pagina
                </MenuItem>
                <MenuItem as="button" onClick={handleSubmit(handleGoToCandidateArea)}>
                  Enviar e ir para Isenções
                </MenuItem>
              </MenuList>
            </Menu>
          </Stack>
        )}
      </Flex>
    </Stack>
  );
};

export default MarrowDonnor;
